import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';

import './Autocomplete.scss';

const defaultOptionRenderer = function DefaultOption(option) {
  return (
    <div>{option.label}</div>
  );
};

const defaultTagRenderer = function defaultTagRenderer(option) {
  return <div>{option.label}</div>;
};

export default class Autocomplete extends Component {
  static propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      unavailable: PropTypes.boolean,
      checked: PropTypes.boolean,
    })),
    optionsHeight: PropTypes.number,
    optionRenderer: PropTypes.func,
    tagRenderer: PropTypes.func,
    onOptionSelect: PropTypes.func.isRequired,
    onOptionDeselect: PropTypes.func.isRequired,
    noResultsMessage: PropTypes.string,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    options: [],
    optionsHeight: 200,
    optionRenderer: defaultOptionRenderer,
    tagRenderer: defaultTagRenderer,
    noResultsMessage: 'No matches found',
    placeholder: 'Search...',
  };

  state = {
    isOpen: false,
    focusedOptionIndex: 0,
    query: '',
  };

  componentDidUpdate() {
    if (this.focusedOptionRef && this.optionListRef) {
      const focusedDOM = ReactDOM.findDOMNode(this.focusedOptionRef);
      const menuDOM = ReactDOM.findDOMNode(this.optionListRef);
      const focusedRect = focusedDOM.getBoundingClientRect();
      const menuRect = menuDOM.getBoundingClientRect();
      if (focusedRect.bottom > menuRect.bottom) {
        menuDOM.scrollTop = (focusedDOM.offsetTop + focusedDOM.clientHeight - menuDOM.offsetHeight);
      } else if (focusedRect.top < menuRect.top) {
        menuDOM.scrollTop = focusedDOM.offsetTop;
      }
    }
  }

  lastMousePos = {
    x: -1,
    y: -1,
  };

  isOptionVisible = (option) => {
    const { query } = this.state;

    return query === '' || option.label.toLowerCase().indexOf(query.toLowerCase()) !== -1;
  };

  getVisibleOptions() {
    const { options } = this.props;
    return options.filter(this.isOptionVisible);
  }

  handleInputBlur = () => {
    this.setState({
      isOpen: false,
    });
  };

  onOptionMouseEnter = (event) => {
    const option = event.currentTarget;
    const index = Number(option.dataset.index);
    const { x, y } = this.lastMousePos;

    if (x !== event.screenX || y !== event.screenY) {
      this.lastMousePos = {
        x: event.screenX,
        y: event.screenY,
      };

      this.setState({
        focusedOptionIndex: index,
      });
    }
  };

  onInputKeyDown = (event) => {
    const { isOpen } = this.state;

    const arrowUpKeyCode = 38;
    const arrowDownKeyCode = 40;
    const enterKeyCode = 13;
    const tabKeyCode = 9;
    const backspaceKeyCode = 8;
    const escapeKeyCode = 27;

    if (event.keyCode === arrowUpKeyCode && isOpen) {
      event.preventDefault();
      this.focusPreviousOption();
    } else if (event.keyCode === arrowDownKeyCode) {
      event.preventDefault();
      if (isOpen) {
        this.focusNextOption();
      } else {
        this.setState({
          isOpen: true,
          focusedOptionIndex: 0,
        });
      }
    } else if (isOpen && event.keyCode === tabKeyCode) {
      this.selectCurrentFocusedOption();
    } else if (event.keyCode === enterKeyCode) {
      if (isOpen) {
        this.selectCurrentFocusedOption();
      } else {
        this.setState({
          isOpen: true,
          focusedOptionIndex: 0,
        });
      }
    } else if (event.keyCode === backspaceKeyCode && this.state.query === '') {
      event.preventDefault();

      if (isOpen) {
        this.setState({
          isOpen: false,
        });
      } else {
        const lastSelectedOption = [...this.props.options].reverse().find(option => option.checked);
        if (lastSelectedOption) {
          this.deselectOption(lastSelectedOption);
        }
      }
    } else if (event.keyCode === escapeKeyCode && isOpen) {
      event.preventDefault();
      this.setState({
        isOpen: false,
      });
    }
  };

  onInputChange = (event) => {
    const selectElement = event.currentTarget;
    this.setState({
      query: selectElement.value,
      focusedOptionIndex: 0,
      isOpen: true,
    });
  };

  onInputClick = (event) => {
    event.preventDefault();

    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
        focusedOptionIndex: 0,
      });
      this.inputRef.focus();
    } else {
      this.setState({
        isOpen: false,
      });
    }
  };

  focusPreviousOption() {
    const { focusedOptionIndex } = this.state;
    const visibleOptions = this.getVisibleOptions();
    const previousVisibleOption = (option, index) => (index >= (visibleOptions.length - focusedOptionIndex)) && !option.unavailable;
    const newIndex = [...visibleOptions].reverse().findIndex(previousVisibleOption);

    if (newIndex !== -1) {
      this.setState({
        focusedOptionIndex: visibleOptions.length - newIndex - 1,
      });
    }
  }

  focusNextOption() {
    const { focusedOptionIndex } = this.state;
    const visibleOptions = this.getVisibleOptions();
    const newIndex = visibleOptions.findIndex((option, index) =>
      index > focusedOptionIndex && !option.unavailable,
    );

    if (newIndex !== -1) {
      this.setState({
        focusedOptionIndex: newIndex,
      });
    }
  }

  onOptionClick = (event) => {
    const { options } = this.props;
    event.preventDefault();

    const optionElement = event.currentTarget;
    const index = Number(optionElement.dataset.index);
    this.selectOption(options[index]);
  };

  selectCurrentFocusedOption() {
    const visibleOptions = this.getVisibleOptions();
    this.selectOption(visibleOptions[this.state.focusedOptionIndex]);
  }

  selectOption(option) {
    if (!option.unavailable) {
      const { onOptionSelect } = this.props;
      onOptionSelect(option);
      this.inputRef.value = '';
      this.setState({
        query: '',
        isOpen: false,
      });
    }
  }

  deselectOption(option) {
    const { onOptionDeselect } = this.props;
    onOptionDeselect(option);
  }

  saveInputRef = ref => this.inputRef = ref;
  saveOptionListRef = ref => this.optionListRef = ref;
  saveFocusedOptionRef = ref => this.focusedOptionRef = ref;

  renderOptions() {
    const { options, optionRenderer } = this.props;
    const { focusedOptionIndex, query } = this.state;
    let visibleIndex = -1;

    return options.map((option, index) => {
      const showOption = this.isOptionVisible(option);

      if (showOption) {
        visibleIndex++;
      }

      const isFocused = visibleIndex === focusedOptionIndex;
      const ref = isFocused ? this.saveFocusedOptionRef : undefined;

      return showOption && (
        <li
          key={option.value}
          data-index={`${index}`}
          onMouseMove={this.onOptionMouseEnter}
          onMouseDown={this.onOptionClick}
          ref={ref}
          className="autocomplete__option-wrapper"
        >
          { optionRenderer(option, isFocused, query) }
        </li>
      );
    }).filter(e => e);
  }

  renderTags() {
    const { options, tagRenderer } = this.props;
    const tagElements = [];

    options.forEach((option) => {
      if (option.checked) {
        const removeFunc = () => { this.deselectOption(option); };
        tagElements.push(<li className="autocomplete__tag-wrapper" key={option.value}>{tagRenderer(option, removeFunc)}</li>);
      }
    });

    return tagElements;
  }

  render() {
    const { optionsHeight, noResultsMessage, placeholder } = this.props;
    const { isOpen } = this.state;
    const optionsStyle = {
      maxHeight: `${optionsHeight}px`,
    };

    const optionElements = this.renderOptions();
    const tagElements = this.renderTags();

    return (
      <div className="autocomplete">
        <ul className="autocomplete__tag-list">
          { tagElements }
        </ul>
        <div className={`autocomplete__input-container autocomplete__input-container--${tagElements.length === 0 ? 'no-results' : 'with-results'}`}>
          <input
            className="autocomplete__input"
            ref={this.saveInputRef}
            onBlur={this.handleInputBlur}
            onKeyDown={this.onInputKeyDown}
            onChange={this.onInputChange}
            onMouseDown={this.onInputClick}
            placeholder={placeholder}
          />
        </div>
        { isOpen && (
          <ul className="autocomplete__option-list" style={optionsStyle} ref={this.saveOptionListRef}>
            { optionElements.length ? optionElements : <li className="autocomplete__option-list__no-results">{noResultsMessage}</li> }
          </ul>
        )}
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/Autocomplete/Autocomplete.jsx
 **/